import React, { createContext, PropsWithChildren, useState, useContext } from "react";

export type ModalType = "informational" | "error" | "action" | "confirmation";

export interface ModalAction {
  text: string;
  onClick: () => void;
  intent: "primary" | "secondary" | "tertiary" | "affirm" | "negate";
  id?: string;
}

export interface ModalConfig {
  isOpen: boolean;
  title: string;
  content: React.ReactNode | string;
  type: ModalType;
  primaryAction?: ModalAction;
  secondaryAction?: ModalAction;
  onClose?: () => void;
  isCloseVisible?: boolean;
}

export interface ModalContextType {
  modalConfig: ModalConfig;
  openModal: (config: Partial<ModalConfig>) => void;
  closeModal: () => void;
  setTitle: (title: string) => void;
  setContent: (content: React.ReactNode | string) => void;
  setType: (type: ModalType) => void;
  setPrimaryAction: (action: ModalAction) => void;
  setSecondaryAction: (action: ModalAction) => void;
}

const defaultModalConfig: ModalConfig = {
  isOpen: false,
  title: "",
  content: "",
  type: "informational",
  isCloseVisible: true,
};

export const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const useModal = () => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
};

export const ModalProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [modalConfig, setModalConfig] = useState<ModalConfig>(defaultModalConfig);

  const openModal = (config: Partial<ModalConfig>) => {
    setModalConfig({
      ...defaultModalConfig,
      ...config,
      isOpen: true,
    });
  };

  const closeModal = () => {
    if (modalConfig.onClose) {
      modalConfig.onClose();
    }
    setModalConfig({ ...modalConfig, isOpen: false });
  };

  const setTitle = (title: string) => {
    setModalConfig({ ...modalConfig, title });
  };

  const setContent = (content: React.ReactNode | string) => {
    setModalConfig({ ...modalConfig, content });
  };

  const setType = (type: ModalType) => {
    setModalConfig({ ...modalConfig, type });
  };

  const setPrimaryAction = (action: ModalAction) => {
    setModalConfig({ ...modalConfig, primaryAction: action });
  };

  const setSecondaryAction = (action: ModalAction) => {
    setModalConfig({ ...modalConfig, secondaryAction: action });
  };

  const contextValue: ModalContextType = {
    modalConfig,
    openModal,
    closeModal,
    setTitle,
    setContent,
    setType,
    setPrimaryAction,
    setSecondaryAction,
  };

  return <ModalContext.Provider value={contextValue}>{children}</ModalContext.Provider>;
};
